import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Luxury underline input — premium, frictionless, brand-aligned
const LuxField = ({ label, name, type = "text", required, value, onChange, multiline }) => {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="num-overline text-ivory/55 text-[10px]">
        {label}
        {required && <span className="text-gold/90 ml-1">•</span>}
      </span>
      <Tag
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        rows={multiline ? 2 : undefined}
        data-testid={`lead-field-${name}`}
        className="mt-1.5 w-full bg-transparent border-b border-ivory/15 focus:border-gold outline-none font-body text-ivory text-[15px] py-2.5 transition-colors duration-500 placeholder:text-ivory/30 resize-none"
        autoComplete="off"
      />
    </label>
  );
};

export default function LeadModal({
  open,
  onOpenChange,
  title,
  microcopy,
  ctaSource,
  showShipping = false,
  confirmation,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    shipping_address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const reset = () => {
    setForm({ name: "", email: "", company: "", country: "", shipping_address: "" });
    setSubmitted(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company || null,
          country: form.country || null,
          shipping_address: showShipping ? form.shipping_address || null : null,
          cta_source: ctaSource,
          source_url: typeof window !== "undefined" ? window.location.href : null,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch (err) {
      setError("We couldn't reach our team just now. Please try again or message us on WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) {
      // Brief delay before resetting so the close animation completes
      setTimeout(reset, 350);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={`lead-modal-${ctaSource}`}
        className="!max-w-xl !bg-ink !text-ivory !border-gold/20 !rounded-none !p-0 !gap-0 overflow-hidden"
      >
        {/* gold hairline at top */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

        <div className="px-8 md:px-12 py-10 md:py-12">
          {!submitted ? (
            <>
              <div className="num-overline text-gold/80">Aarya24KT · Partnership</div>
              <h3 className="mt-4 font-heading text-ivory text-3xl md:text-[40px] leading-[1.1] tracking-tightest">
                {title}
              </h3>
              {microcopy && (
                <p className="mt-4 font-body text-[14px] leading-[1.7] text-beige/70 max-w-md">
                  {microcopy}
                </p>
              )}

              <form onSubmit={submit} className="mt-10 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <LuxField label="Name" name="name" required value={form.name} onChange={handle} />
                  <LuxField label="Email" name="email" type="email" required value={form.email} onChange={handle} />
                  <LuxField label="Company" name="company" value={form.company} onChange={handle} />
                  <LuxField label="Country" name="country" value={form.country} onChange={handle} />
                </div>
                {showShipping && (
                  <LuxField
                    label="Shipping Address (optional)"
                    name="shipping_address"
                    value={form.shipping_address}
                    onChange={handle}
                    multiline
                  />
                )}

                {error && (
                  <div className="num-overline text-rose-300/80" data-testid="lead-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-submit-btn"
                  className="group inline-flex items-center gap-4 bg-gold px-8 py-4 text-[11px] uppercase tracking-[0.28em] text-ink hover:bg-ivory disabled:opacity-60 transition-colors duration-500 ease-luxe"
                >
                  <span>{submitting ? "Sending…" : "Submit Request"}</span>
                  <span className="font-heading italic text-base transition-transform duration-500 ease-luxe group-hover:translate-x-1">
                    →
                  </span>
                </button>

                <p className="num-overline text-ivory/35 text-[10px] pt-2">
                  Your details remain confidential. No marketing communication.
                </p>
              </form>
            </>
          ) : (
            // CONFIRMATION VIEW
            <div className="py-6" data-testid="lead-confirmation">
              <div className="num-overline text-gold/80">Thank you</div>
              <h3 className="mt-4 font-heading text-ivory text-3xl md:text-[40px] leading-[1.15] tracking-tightest max-w-md">
                {confirmation}
              </h3>
              <div className="mt-10 h-px w-16 bg-gold/40" />
              <p className="mt-6 font-body text-[14px] leading-[1.7] text-beige/65 max-w-md">
                Until then — explore the rest of the Aarya24KT presentation, or
                begin a private conversation on WhatsApp.
              </p>
              <button
                onClick={() => handleOpenChange(false)}
                data-testid="lead-close-btn"
                className="mt-8 inline-flex items-center gap-3 border border-gold/40 px-6 py-3 text-[11px] uppercase tracking-[0.28em] text-ivory hover:border-gold hover:text-gold transition-colors duration-500"
              >
                <span>Close</span>
              </button>
            </div>
          )}
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      </DialogContent>
    </Dialog>
  );
}
